import {
  ApiResponse,
  BroadcastEstimateRequest,
  BroadcastEstimateResponse,
  BroadcastFilter,
  BroadcastTranslateRequest,
  BroadcastTranslateResponse,
  BroadcastTestSendRequest,
  BroadcastTestSendResponse,
  BroadcastContentMap,
  BroadcastSendRequest,
  BroadcastSendResponse,
  BroadcastListResponse,
  BroadcastDetailResponse,
} from '@mygate/common';
import { api } from './api';

class BroadcastsService {
  /**
   * Get the current audience estimate (recipient counts by language/platform + remembered test phone).
   * Omitting `filter` falls back to the legacy default audience (ACTIVE users).
   */
  async getEstimate(filter?: BroadcastFilter): Promise<ApiResponse<BroadcastEstimateResponse>> {
    const body: BroadcastEstimateRequest = filter ? { filter } : {};
    return api.post('/api/staff/broadcasts/estimate', body);
  }

  /**
   * Translate a source language's title/body into the requested target languages via LLM.
   */
  async translate(request: BroadcastTranslateRequest): Promise<ApiResponse<BroadcastTranslateResponse>> {
    return api.post('/api/staff/broadcasts/translate', request);
  }

  /**
   * Send a test broadcast push to a single phone number.
   */
  async sendTest(request: BroadcastTestSendRequest): Promise<ApiResponse<BroadcastTestSendResponse>> {
    return api.post('/api/staff/broadcasts/test', request);
  }

  /**
   * Send the broadcast to the full audience. This is the real, irreversible send.
   * Omitting `filter` falls back to the legacy default audience (ACTIVE users).
   */
  async send(
    content: BroadcastContentMap,
    filter?: BroadcastFilter,
    idempotencyKey?: string,
  ): Promise<ApiResponse<BroadcastSendResponse>> {
    const request: BroadcastSendRequest = {
      content,
      ...(filter ? { filter } : {}),
      ...(idempotencyKey ? { idempotencyKey } : {}),
    };
    return api.post('/api/staff/broadcasts', request);
  }

  /**
   * List past broadcasts (server-side paginated).
   */
  async list(page = 1, limit = 20): Promise<ApiResponse<BroadcastListResponse>> {
    return api.get(`/api/staff/broadcasts?page=${page}&limit=${limit}`);
  }

  /**
   * Get full detail (content, audience snapshot, per-language stats) for a single broadcast.
   */
  async getById(id: string): Promise<ApiResponse<BroadcastDetailResponse>> {
    return api.get(`/api/staff/broadcasts/${id}`);
  }
}

export const broadcastsService = new BroadcastsService();
